import React, { useState } from 'react';
import moment from 'moment';
import { I18n } from '@aws-amplify/core';
import { useHistory } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, MoreOutlined, ReconciliationOutlined } from '@ant-design/icons';

import { ModalDelete } from '@/components/ui/Modals/ModalDelete';
import { ACTION_PLAN_STATUS_CONFIG } from '../constants';
import { dateFormat } from '@/utils/date-format';
import type { ActionPlan } from '../types';
import * as S from '../../styles';

interface ActionPlanCardProps {
	item: ActionPlan;
	companyId: string;
	onEdit: (id: string) => void;
	onDelete: (id: string) => void;
}

export function ActionPlanCard({ item, companyId, onEdit, onDelete }: Readonly<ActionPlanCardProps>): JSX.Element {
	const history = useHistory();
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const dueDateFormatted = item.due_date ? moment(item.due_date).format(dateFormat()) : '-';
	const daysLeft = item.due_date ? moment(item.due_date).diff(moment(), 'day') : null;
	const responsibleName = item.responsible_user?.name ?? '-';
	const statusConfig = item.status ? ACTION_PLAN_STATUS_CONFIG[item.status] : null;

	const menuItems = [
		{
			key: 'edit',
			icon: <EditOutlined />,
			label: I18n.get('Edit'),
			style: { color: '#002fbb' },
			onClick: () => onEdit(item.id)
		},
		{
			key: 'view',
			icon: <ReconciliationOutlined />,
			label: I18n.get('View'),
			style: { color: '#002fbb' },
			onClick: () => history.push(`/action-plans?action_plan_id=${item.id}&company_id=${companyId}`)
		},
		{
			key: 'delete',
			icon: <DeleteOutlined />,
			label: I18n.get('Delete'),
			danger: true,
			onClick: () => setDeleteModalOpen(true)
		}
	];

	return (
		<>
			<ModalDelete
				open={deleteModalOpen}
				description={I18n.get('Are you sure you want to delete the action plan') + ` "${item.title}"?`}
				onCancel={() => setDeleteModalOpen(false)}
				onConfirm={() => {
					onDelete(item.id);
					setDeleteModalOpen(false);
				}}
			/>
			<S.CardWrapper>
				<S.CardHeader>
					<S.CardTitle>{item.title}</S.CardTitle>
				</S.CardHeader>
				<S.CardMenuButton>
					<S.ActionPlanCardDropdown menu={{ items: menuItems }} trigger={['click']}>
						<S.CardMenuTrigger>
							<MoreOutlined style={{ fontSize: 16, rotate: '90deg' }} />
						</S.CardMenuTrigger>
					</S.ActionPlanCardDropdown>
				</S.CardMenuButton>
				<S.CardBody>
					<S.MetaRowGroup>
						<S.MetaRow>
							<S.MetaLabel>{I18n.get('Responsible')}:</S.MetaLabel>
							<S.MetaValue>{responsibleName}</S.MetaValue>
						</S.MetaRow>
						<S.MetaRowCenter>
							<S.MetaLabel>{I18n.get('Deadline:')}</S.MetaLabel>
							<S.MetaValue>
								{dueDateFormatted}
								{daysLeft !== null && ` (${daysLeft} ${I18n.get('days')})`}
							</S.MetaValue>
						</S.MetaRowCenter>
						<S.MetaRowEnd>
							<S.MetaLabel>{I18n.get('Status')}:</S.MetaLabel>
							{statusConfig && (
								<S.StatusGroup>
									<S.StatusDot $color={statusConfig.color} />
									<S.MetaValue>{I18n.get(statusConfig.label)}</S.MetaValue>
								</S.StatusGroup>
							)}
						</S.MetaRowEnd>
					</S.MetaRowGroup>
					{item.description && (
						<S.DescriptionRow>
							<S.MetaLabel>{I18n.get('Description')}:</S.MetaLabel>
							<S.MetaValue>{item.description}</S.MetaValue>
						</S.DescriptionRow>
					)}
				</S.CardBody>
				{item.tasks.length > 0 && (
					<>
						<S.CardDivider />
						<S.TaskSubHeader>{I18n.get('Tasks')}</S.TaskSubHeader>
						{item.tasks.map((task) => (
							<S.TaskRow key={task.id}>
								<S.TaskCell>{task.title}</S.TaskCell>
								<S.TaskCellDivider />
								<S.TaskCell>{task.responsible_user?.name ?? '-'}</S.TaskCell>
								<S.TaskCellDivider />
								<S.TaskCell>{task.due_date ? moment(task.due_date).format(dateFormat()) : '-'}</S.TaskCell>
							</S.TaskRow>
						))}
					</>
				)}
			</S.CardWrapper>
		</>
	);
}
